import { useCallback } from "react";

import { useRequestWithFeedback } from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { isEmptyObject } from "../../../utils";

import { API } from "./api";

export const useNpsRequests = () => {
  const { send: fetchSurveysSend } = useRequestWithFeedback<
    API.NPS.FetchSurveys.Response,
    API.NPS.FetchSurveys.Error
  >();

  const fetchSurveys = useCallback(() => {
    return new Promise<API.NPS.Survey | null>((resolve) => {
      fetchSurveysSend({
        action: API.fetchSurveys(),
        onData: (survey) => {
          if (isEmptyObject(survey)) {
            resolve(null);
          } else {
            resolve(survey as API.NPS.Survey);
          }
        },
        onError: () => resolve(null),
      });
    });
  }, [fetchSurveysSend]);

  const { send: recordImpressionSend } = useRequestWithFeedback<
    API.NPS.RecordImpression.Response,
    API.NPS.RecordImpression.Error
  >();

  const recordImpression = useCallback(
    (params: API.NPS.RecordImpression.Request) => {
      return new Promise<API.NPS.RecordImpression.Response | null>(
        (resolve) => {
          recordImpressionSend({
            action: API.recordImpression(params),
            onData: (survey) => resolve(survey),
            onError: () => resolve(null),
          });
        },
      );
    },
    [recordImpressionSend],
  );

  const { send: submitAnswersSend, loading: submitAnswersLoading } =
    useRequestWithFeedback<
      API.NPS.SubmitAnswers.Response,
      API.NPS.SubmitAnswers.Error
    >();

  const submitAnswer = useCallback(
    (params: API.NPS.SubmitAnswers.Request) => {
      return new Promise<boolean>((resolve) => {
        submitAnswersSend({
          action: API.submitAnswer(params),
          onData: () => resolve(true),
          onError: () => resolve(false),
        });
      });
    },
    [submitAnswersSend],
  );

  return useObjectMemo({
    fetchSurveys,
    recordImpression,
    submitAnswer,
    submitAnswersLoading,
  });
};
